import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { useMemo } from 'react';
import { useState } from 'react';
import { observer } from '@formily/reactive-react';
import { Select } from '@formily/antd-v5';
import { IFormFieldSchema } from '../../types';
import { SelectProps } from 'antd/es/select';
import { v4 as uuidv4 } from 'uuid';

const MULTIPLE_MODE = 'multiple';
const TAGS_MODE = 'tags';

export interface IFormSelectProps extends SelectProps {
  visible?: boolean;
  multipleName?: string;
}

export interface IProps {
  schema?: IFormFieldSchema<IFormSelectProps>;
}

const FormSelect = observer(({ schema }: IProps = {}) => {
  const field = useField<Field>();
  const componentProps = schema
    ? ((schema['x-component-props'] ?? {}) as IFormSelectProps)
    : ((field.componentProps ?? {}) as IFormSelectProps);

  const { multipleName = 'value', mode } = componentProps;

  // Cache default selected values in order to keep uuid consistent
  // Use ?? instead of || so a legitimate `false` value isn't discarded as empty
  const [defaultSelected] = useState(() => schema?.default ?? field.value);

  // Get value from schema or field
  const value = schema?.default ?? field.value ?? [];

  // Find selected values if multiple mode transform to array of values
  // If single mode simply return the value
  const selectedValue = useMemo(() => {
    if (mode === MULTIPLE_MODE || mode === TAGS_MODE) {
      // Type checking defaultSelected to array if it is not an array because of build mode where we return key
      return Array.isArray(value)
        ? value
            ?.filter((v: any) => !v.toBeDeleted)
            ?.map((v: any) => v[multipleName])
        : [];
    }

    return value;
  }, [mode, schema, field.value]);

  // Generate options with ids for multiple mode
  // If the option is not in the default selected values, generate a new id
  // If option is in the default selected values, use the id from the default selected values
  // If single mode, return the options as is
  const options = useMemo(() => {
    if (mode === MULTIPLE_MODE || mode === TAGS_MODE) {
      // Type checking defaultSelected to array if it is not an array because of build mode where we return key
      const defaultSelectedArray = Array.isArray(defaultSelected)
        ? defaultSelected
        : [];
      const items =
        componentProps.options?.map(option => ({
          ...option,
          id:
            defaultSelectedArray?.find(
              (v: any) => v[multipleName] === option.value
            )?.id || uuidv4(),
        })) || [];

      // If the default selected item is not in the options, add it to the options
      // This is important for tags since they can be without options
      defaultSelectedArray?.forEach((selectedItem: any) => {
        if (
          selectedItem[multipleName] &&
          !items.find((item: any) => item.value === selectedItem[multipleName])
        ) {
          items.push({
            value: selectedItem[multipleName],
            id: selectedItem.id,
          });
        }
      });

      return items;
    }
    return componentProps.options;
  }, [componentProps, value]);

  const handleSelect = (value: string | string[]) => {
    if (Array.isArray(value)) {
      // New selected values mapping
      const newSelectedValues = value.map((v: any) => ({
        [multipleName]: v,
        id: options?.find((o: any) => o.value === v)?.id || uuidv4(),
      }));

      const defaultSelectedValues =
        defaultSelected?.map((v: any) => v[multipleName]) || [];

      // If default selected values are not in the new selected values, add them to the new selected values with toBeDeleted flag
      defaultSelectedValues.forEach((defaultSelectedValue: any) => {
        if (!value.includes(defaultSelectedValue)) {
          newSelectedValues.push({
            [multipleName]: defaultSelectedValue,
            id: options?.find((o: any) => o.value === defaultSelectedValue)?.id,
            toBeDeleted: true,
          });
        }
      });

      field.setValue(newSelectedValues);
    } else {
      const nullableValue = value === undefined ? null : value;
      field.setValue(nullableValue);
    }
    field.validate();
  };

  const { visible, ...rest } = componentProps;

  if (visible === false) return null;

  return (
    <Select
      {...rest}
      disabled={field.disabled}
      onChange={handleSelect}
      value={selectedValue}
    />
  );
});

export default FormSelect;
